/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { Fragment, memo } from 'react'
import { jsx, css } from '@emotion/react'
import { isEqual, sortBy } from 'lodash'

import { useEuiTheme } from '@elastic/eui'

import type { AllocatorVizInstance, RegionId, SliderInstanceType } from '@modules/ui-types'

import { useSliderColorPicker } from '@/lib/sliders/colors'

import toPercentage from '../../lib/toPercentage'
import { resolveDeploymentUrlForAnyCluster } from '../../lib/urlBuilder'
import prettySize from '../../lib/prettySize'
import { getInstanceId, getMemoryCapacity } from '../../lib/allocatorInstanceCapacitiesViz'

import { useCapacityColorPicker } from './capacityVizColors'

import type { FC } from 'react'

type Props = {
  regionId: RegionId
  instances: AllocatorVizInstance[]
  usedCapacity: number
  totalCapacity: number
  largestCapacity: number
  hideInstanceKindsHairline?: boolean
  isCritical?: boolean
}

const useStyles = () => {
  const { euiTheme } = useEuiTheme()

  return {
    allocatorCapacity: css({
      display: 'flex',
      height: '1rem',
      '& > *:first-child': {
        borderTopLeftRadius: euiTheme.border.radius.small,
        borderBottomLeftRadius: euiTheme.border.radius.small,
      },
      '& > *:last-child': {
        borderTopRightRadius: euiTheme.border.radius.small,
        borderBottomRightRadius: euiTheme.border.radius.small,
      },
    }),
    allocatorCapacityKinds: css({
      display: 'block',
      width: '100%',
      height: '4px',
      marginTop: '3px',
      pointerEvents: 'none',
    }),
    allocatorCapacityKind: css({
      height: '4px',
    }),
    allocatorCapacityUsed: css({
      [`@media only screen and (max-width: ${700 - 0.00000001}px)`]: {
        pointerEvents: 'none',
        cursor: 'default',
      },
    }),
    allocatorCapacityUnused: css({
      [`@media only screen and (max-width: ${700 - 0.00000001}px)`]: {
        pointerEvents: 'none',
        cursor: 'default',
      },
    }),
    allocatorCapacityUnusedColor: css({
      backgroundColor: euiTheme.colors.lightShade,
      backgroundImage: `repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 5px,
        rgba(255,255,255,.5) 5px,
        rgba(255,255,255,.5) 10px
      )`,
      '.euiTheme-dark &': {
        backgroundColor: euiTheme.colors.lightShade,
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 5px,
          rgba(255,255,255,.2) 5px,
          rgba(255,255,255,.2) 10px
        )`,
      },
      '.isUnhealthy &': {
        backgroundColor: 'rgba(255, 0, 0, 0.15)',
      },
    }),
  }
}

const InstanceCapacitiesViz: FC<Props> = ({
  totalCapacity,
  largestCapacity,
  instances,
  usedCapacity,
  hideInstanceKindsHairline,
  regionId,
  isCritical,
}) => {
  const sortedInstances = sortBy(instances, (instance) => -getMemoryCapacity(instance))
  const widthPercent = toPercentage(totalCapacity / largestCapacity)
  const width = `${widthPercent}%`
  const styles = useStyles()

  return (
    <div style={{ width }}>
      <div css={styles.allocatorCapacity}>
        <UsedCapacity
          instances={sortedInstances}
          totalCapacity={totalCapacity}
          regionId={regionId}
          isCritical={isCritical}
        />
        <UnusedCapacity usedCapacity={usedCapacity} totalCapacity={totalCapacity} />
      </div>

      {hideInstanceKindsHairline ? null : (
        <InstanceKinds instances={sortedInstances} totalCapacity={totalCapacity} />
      )}
    </div>
  )
}

const UsedCapacity = ({
  instances,
  totalCapacity,
  regionId,
  isCritical,
}: {
  instances: AllocatorVizInstance[]
  totalCapacity: number
  regionId: RegionId
  isCritical?: boolean
}) => {
  const getCapacityColor = useCapacityColorPicker()
  const { euiTheme } = useEuiTheme()
  const styles = useStyles()

  return (
    <Fragment>
      {instances.map((instance, index) => {
        const alternate = index % 2 !== 0
        const { clusterId, kind, stackDeploymentId } = instance
        const instanceId = getInstanceId(instance)
        const capacity = getMemoryCapacity(instance)
        const usage = toPercentage(capacity / totalCapacity)
        const displayId = clusterId.slice(0, 6)
        const backgroundColor = isCritical
          ? euiTheme.colors.darkestShade
          : getCapacityColor(capacity, alternate)

        const tooltip = `${prettySize(capacity)} ${kind} (${displayId})`

        // The reason we're not using `<Link>` from `react-router` is that
        // it considerably slowed down unmounting the visualizations.
        return (
          <a
            key={`${clusterId}-${kind}-${instanceId}`}
            href={resolveDeploymentUrlForAnyCluster({
              regionId,
              clusterId,
              sliderInstanceType: kind,
              stackDeploymentId,
            })}
            css={styles.allocatorCapacityUsed}
            data-tooltip={tooltip}
            style={{
              width: `${usage}%`,
              backgroundColor,
            }}
          />
        )
      })}
    </Fragment>
  )
}

const UnusedCapacity = ({
  usedCapacity,
  totalCapacity,
}: {
  usedCapacity: number
  totalCapacity: number
}) => {
  const styles = useStyles()

  if (usedCapacity === totalCapacity) {
    return null
  }

  const unused = 100 - toPercentage(usedCapacity / totalCapacity)
  const unusedSpace = totalCapacity - usedCapacity
  const tooltip = `${prettySize(unusedSpace)} of available capacity`

  return (
    <div
      css={[styles.allocatorCapacityUnused, styles.allocatorCapacityUnusedColor]}
      data-tooltip={tooltip}
      style={{
        width: `${unused}%`,
      }}
    />
  )
}

const InstanceKinds = ({
  instances,
  totalCapacity,
}: {
  instances: AllocatorVizInstance[]
  totalCapacity: number
}) => {
  const kinds = [] as Array<{
    kind: SliderInstanceType
    width: number
    start: number
    color?: string
  }>

  const getSliderColor = useSliderColorPicker()
  const styles = useStyles()

  instances.forEach((instance) => {
    const { kind } = instance
    const width = toPercentage(getMemoryCapacity(instance) / totalCapacity)
    const lastKind = kinds[kinds.length - 1]
    const isSameKind = lastKind && lastKind.kind === kind

    if (isSameKind) {
      lastKind.width += width
      return
    }

    const start = lastKind ? lastKind.start + lastKind.width : 0
    const color = getSliderColor(kind)

    kinds.push({ kind, width, start, color })
  })

  return (
    <svg css={styles.allocatorCapacityKinds}>
      {kinds.map(({ kind, start, width, color }) => (
        <rect
          key={`nodes-${kind}:${start}:${width}`}
          x={`${start}%`}
          width={`${width}%`}
          fill={color}
          css={styles.allocatorCapacityKind}
          rx='1'
          ry='1'
        />
      ))}
    </svg>
  )
}

const arePropsEqual = (prevProps, nextProps) => isEqual(prevProps, nextProps)

export default memo(InstanceCapacitiesViz, arePropsEqual)
